import type { IngredientView, StockBalanceView } from "@kopi/contracts";
import {
  DEFAULT_STOCK_BALANCE_SORT,
  definedOnly,
  type SortValue,
  STOCK_BALANCE_SORTS,
} from "@kopi/contracts/client";
import { STOCK_POLICIES, type StockPolicy } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Combobox } from "@kopi/ui/components/combobox";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@kopi/ui/components/tabs";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BellIcon, PlusIcon, TriangleAlertIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { stock } from "@/i18n/messages/stock";
import { type AreaTranslate, type useI18n, useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletsQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";
import { CountsTab, ReceiptsTab, stockKey, ThresholdDialog } from "@/stock/documents";

/** The balance list's column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(STOCK_BALANCE_SORTS);

export const Route = createFileRoute("/_authed/stock")({
  staticData: { section: "workspace", crumb: "nav.stock" },
  validateSearch,
  component: StockPage,
});

type T = AreaTranslate<typeof stock.id>;
const balancesQuery = (
  outletId: string,
  q: string,
  low: boolean,
  sort?: SortValue<(typeof STOCK_BALANCE_SORTS)[number]>,
) =>
  infiniteQueryOptions({
    queryKey: [...stockKey, "balances", outletId, q, low, sort],
    enabled: Boolean(outletId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId }).stock.balances.get({
          query: {
            limit: 30,
            ...definedOnly({ q, sort, cursor: pageParam }),
            ...(low ? { low: true } : {}),
          },
        }),
      ),
  });

const policyQuery = (outletId: string) =>
  queryOptions({
    queryKey: [...stockKey, "policy", outletId],
    enabled: Boolean(outletId),
    queryFn: async () => unwrap(await api.v1.outlets({ outletId }).stock.policy.get()),
  });

function columnsFor(t: T, format: ReturnType<typeof useI18n>["format"]): DataTableColumn<StockBalanceView>[] {
  const amount = (value: string, unit: string) =>
    t("stock.amount", { quantity: format.quantity(value), unit });
  return [
    {
      id: "ingredient",
      header: t("stock.ingredient"),
      enableSorting: true,
      meta: { card: "title" },
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{row.original.ingredientName}</span>
          {row.original.low && <Badge variant="warning">{t("stock.lowBadge")}</Badge>}
        </span>
      ),
    },
    {
      id: "onHand",
      header: t("stock.onHand"),
      cell: ({ row }) => (
        <span className="tabular-nums">{amount(row.original.onHand, row.original.baseUnit)}</span>
      ),
    },
    {
      id: "reserved",
      header: t("stock.reserved"),
      cell: ({ row }) => (
        <span className="tabular-nums">{amount(row.original.reserved, row.original.baseUnit)}</span>
      ),
    },
    {
      id: "available",
      header: t("stock.available"),
      cell: ({ row }) => {
        const low = row.original.available.startsWith("-") || row.original.available === "0";
        return (
          <Badge variant={low ? "destructive" : "outline"} className="tabular-nums">
            {amount(row.original.available, row.original.baseUnit)}
          </Badge>
        );
      },
    },
    {
      id: "avgCost",
      header: t("stock.avgCost"),
      cell: ({ row }) => (
        <span className="tabular-nums text-muted-foreground">
          {t("stock.perUnit", {
            cost: format.quantity(row.original.avgUnitCost),
            unit: row.original.baseUnit,
          })}
        </span>
      ),
    },
  ];
}

/**
 * Stock (R1-22, INV-01): an outlet's ingredient balances — on hand, reserved by orders waiting for
 * preparation, available and moving-average cost — its stock policy, and opening balances. Every
 * figure is the projection of the append-only ledger.
 */
function StockPage() {
  const { t, format } = useMessages(stock);
  const columns = useMemo(() => columnsFor(t, format), [t, format]);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const outlets = useQuery(outletsQuery).data ?? [];
  const { outlet: current } = useCurrentOutlet();
  const keeps = (id: string) =>
    (["inventory.count", "inventory.receive", "inventory.adjust"] as const).some((p) => canAt(scopes, p, id));
  // A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine).
  const choices = outlets.filter((o) => o.active && keeps(o.id));
  const [picked, setPicked] = useState<string | null>(null);
  const outletId = picked ?? choices.find((o) => o.id === current?.id)?.id ?? choices[0]?.id ?? "";
  const mayAdjust = Boolean(outletId) && canAt(scopes, "inventory.adjust", outletId);
  const mayReceive = Boolean(outletId) && canAt(scopes, "inventory.receive", outletId);
  const mayCount = Boolean(outletId) && canAt(scopes, "inventory.count", outletId);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "low">("all");
  const [opening, setOpening] = useState(false);
  const [thresholdFor, setThresholdFor] = useState<StockBalanceView | null>(null);
  const { sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sorting = urlSorting(STOCK_BALANCE_SORTS, DEFAULT_STOCK_BALANCE_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const balances = useInfiniteQuery(balancesQuery(outletId, q, filter === "low", sort));
  const lowList = useInfiniteQuery(balancesQuery(outletId, "", true));
  const lowCount = lowList.data?.pages.reduce((n, page) => n + page.items.length, 0) ?? 0;

  if (choices.length === 0) {
    return (
      <Page>
        <PageHeader title={t("nav.stock")} subtitle={t("stock.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={t("nav.stock")}
        subtitle={t("stock.subtitle")}
        actions={
          mayAdjust && (
            <Button type="button" onClick={() => setOpening(true)}>
              <PlusIcon />
              {t("stock.opening")}
            </Button>
          )
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
        <OptionSelect
          aria-label={t("outlet.heading")}
          className="md:w-56"
          options={choices.map((o) => ({ value: o.id, label: o.name }))}
          value={outletId}
          onValueChange={setPicked}
        />
        {lowCount > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setFilter("low")}
            data-testid="low-stock-alert"
          >
            <TriangleAlertIcon className="text-warning" />
            {t("stock.lowCount", { count: lowCount })}
          </Button>
        )}
      </div>
      <Tabs defaultValue="balances">
        <TabsList>
          <TabsTrigger value="balances">{t("stock.tab.balances")}</TabsTrigger>
          <TabsTrigger value="receipts">{t("stock.tab.receipts")}</TabsTrigger>
          <TabsTrigger value="counts">{t("stock.tab.counts")}</TabsTrigger>
        </TabsList>
        <TabsContent value="balances" className="flex flex-col gap-4 pt-4">
          <PolicyCard outletId={outletId} editable={mayAdjust} />
          <DataTable
            paging="pages"
            label={t("stock.list")}
            columns={columns}
            query={balances}
            getRowId={(b) => b.ingredientId}
            search={{ value: q, onChange: setQ, placeholder: t("stock.search") }}
            {...sorting}
            filters={
              <OptionSelect
                aria-label={t("stock.filter.low")}
                className="md:w-48"
                options={[
                  { value: "all", label: t("stock.filter.all") },
                  { value: "low", label: t("stock.filter.low") },
                ]}
                value={filter}
                onValueChange={setFilter}
              />
            }
            rowActions={(balance) =>
              mayAdjust
                ? [
                    {
                      label: t("stock.threshold"),
                      icon: <BellIcon />,
                      onSelect: () => setThresholdFor(balance),
                    },
                  ]
                : []
            }
            empty={{ title: t("stock.emptyTitle"), description: t("stock.emptyHint") }}
          />
        </TabsContent>
        <TabsContent value="receipts" className="pt-4">
          <ReceiptsTab outletId={outletId} mayRecord={mayReceive} mayReview={mayAdjust} />
        </TabsContent>
        <TabsContent value="counts" className="pt-4">
          <CountsTab outletId={outletId} mayRecord={mayCount} mayReview={mayAdjust} />
        </TabsContent>
      </Tabs>
      {opening && <OpeningDialog outletId={outletId} onClose={() => setOpening(false)} />}
      {thresholdFor && (
        <ThresholdDialog outletId={outletId} balance={thresholdFor} onClose={() => setThresholdFor(null)} />
      )}
    </Page>
  );
}

function PolicyCard(props: { outletId: string; editable: boolean }) {
  const { t } = useMessages(stock);
  const queryClient = useQueryClient();
  const policy = useQuery(policyQuery(props.outletId));
  const save = useMutation({
    mutationFn: async (next: StockPolicy) =>
      unwrap(
        await api.v1.outlets({ outletId: props.outletId }).stock.policy.put({
          policy: next,
          expectedRevision: policy.data?.revision ?? 1,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: stockKey });
      toast.success(t("stock.policySaved"));
    },
    onError: (error) => {
      toast.error(
        t(
          error instanceof ApiRequestError && error.code === "stock_policy_revision_conflict"
            ? "stock.policyConflict"
            : "stock.failed",
        ),
      );
      void policy.refetch();
    },
  });
  const value = policy.data?.policy ?? "track";
  return (
    <section className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold">{t("stock.policy")}</h2>
        <p className="text-muted-foreground text-sm">{t(`stock.policyHint.${value}`)}</p>
      </div>
      {/* Two fixed choices (CODING_STANDARDS §5: OptionSelect). */}
      <OptionSelect
        aria-label={t("stock.policy")}
        className="sm:w-72"
        disabled={!props.editable || save.isPending || !policy.data}
        options={STOCK_POLICIES.map((p) => ({ value: p, label: t(`stock.policy.${p}`) }))}
        value={value}
        onValueChange={(next) => next !== value && save.mutate(next)}
      />
    </section>
  );
}

const fetchIngredients = async (q: string, cursor: string | undefined) =>
  unwrap(await api.v1.ingredients.get({ query: { limit: 30, active: true, ...definedOnly({ q, cursor }) } }));

function OpeningDialog(props: { outletId: string; onClose: () => void }) {
  const { t } = useMessages(stock);
  const queryClient = useQueryClient();
  const [ingredient, setIngredient] = useState<IngredientView | null>(null);
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const ids = { ingredient: useId(), quantity: useId(), cost: useId() };
  const valid =
    ingredient !== null &&
    /^\d+(\.\d{1,6})?$/.test(quantity) &&
    /[1-9]/.test(quantity) &&
    /^\d+(\.\d{1,6})?$/.test(unitCost);

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.outlets({ outletId: props.outletId }).stock.opening.post({
          ingredientId: ingredient?.id ?? "",
          quantity,
          unitCost,
        }),
      ),
    onSuccess: async (balance) => {
      await queryClient.invalidateQueries({ queryKey: stockKey });
      toast.success(t("stock.openingSaved", { name: balance.ingredientName }));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("stock.opening")}
      description={t("stock.openingHint")}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="opening-stock" disabled={!valid || save.isPending}>
          {t(save.isPending ? "stock.saving" : "stock.save")}
        </Button>
      }
    >
      <form id="opening-stock" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.ingredient}>{t("stock.ingredient")}</Label>
          <Combobox
            id={ids.ingredient}
            fetchPage={fetchIngredients}
            getValue={(i: IngredientView) => i.id}
            getLabel={(i: IngredientView) => `${i.name} (${i.baseUnit.code})`}
            value={ingredient?.id ?? null}
            onChange={(_, picked) => setIngredient(picked)}
            placeholder={t("stock.pickIngredient")}
            searchPlaceholder={t("stock.searchIngredients")}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.quantity}>
              {ingredient ? t("stock.quantity", { unit: ingredient.baseUnit.code }) : t("stock.quantityAny")}
            </Label>
            <Input
              id={ids.quantity}
              inputMode="decimal"
              autoComplete="off"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value.replace(",", ".").trim())}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.cost}>{t("stock.unitCost")}</Label>
            <Input
              id={ids.cost}
              inputMode="decimal"
              autoComplete="off"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value.replace(",", ".").trim())}
            />
          </div>
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              save.error instanceof ApiRequestError && save.error.code === "opening_exists"
                ? "stock.openingExists"
                : "stock.failed",
            )}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
